/**
 * Barre de sous-onglets de l'Accalmie (A98, étendue en A100-3).
 *
 * Un seul composant pour la Boutique (Normal / Corrupted) et l'onglet Dés
 * (Équipés / Inventaire / Recycleur / Sorts) : boutons `.f-tab` en rangée,
 * motif ARIA « tablist » (flèches gauche / droite, tabulation sur l'actif).
 * Le DOM de la Boutique reste identique à celui d'avant (`data-role` et classes
 * passés en options).
 */

export interface SubtabOptions<T extends string> {
  /** Sous-onglets, dans l'ordre d'affichage. */
  tabs: readonly T[];
  labels: Readonly<Record<T, string>>;
  active: T;
  /** Nom accessible de la barre (« Boutique », « Dés »). */
  ariaLabel: string;
  /** `data-role` de la barre. */
  navRole: string;
  /** `data-role` de chaque bouton ; son `data-subtab` porte l'identifiant. */
  tabRole: string;
  /** Classe CSS de la barre (en plus de `f-tabs`) et du bouton (en plus de `f-tab`). */
  navClass: string;
  tabClass: string;
  onPick: (tab: T) => void;
}

export function buildSubtabNav<T extends string>(o: SubtabOptions<T>): HTMLElement {
  const nav = document.createElement('nav');
  nav.className = `f-tabs ${o.navClass}`;
  nav.setAttribute('role', 'tablist');
  nav.setAttribute('aria-label', o.ariaLabel);
  nav.setAttribute('data-role', o.navRole);
  const buttons: HTMLButtonElement[] = [];
  for (const tab of o.tabs) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = `f-tab ${o.tabClass}`;
    btn.setAttribute('role', 'tab');
    btn.setAttribute('data-role', o.tabRole);
    btn.setAttribute('data-subtab', tab);
    btn.setAttribute('aria-selected', String(tab === o.active));
    btn.tabIndex = tab === o.active ? 0 : -1;
    btn.textContent = o.labels[tab];
    btn.addEventListener('click', () => {
      if (tab !== o.active) o.onPick(tab);
    });
    // Flèches gauche / droite : le sous-onglet voisin (le dernier boucle sur le premier).
    btn.addEventListener('keydown', (e) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      e.preventDefault();
      const i = buttons.indexOf(btn);
      const next = buttons[(i + (e.key === 'ArrowRight' ? 1 : buttons.length - 1)) % buttons.length];
      next?.click();
    });
    buttons.push(btn);
    nav.appendChild(btn);
  }
  return nav;
}
